import { apiHosts } from "@/api/constants/apiHosts";
import { LE } from "@/constants/enums";
import type { RequestValidationError } from "@/helpers/requestValidationError";
import type { Incident, IncidentNew } from "@/views/incident/incident";
import { NotifyOptionsError, NotifyOptionsSuccess } from "@modernary/mui/components/notifications";
import { apiService, type Paging, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import type { AxiosResponse } from "axios";
import IncidentService from "./incidentService";

const URL = "incidents";
const ENTITY_NAME = "Incident";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_IHEER_API_BASE_URL,
};

const api = apiService<Incident, Paging<Incident>>(apiHosts.IHEER, axiosParams);
const canDeleteApi = apiService<RequestValidationError>(apiHosts.IHEER, axiosParams);

/**
 * @param {Object} params Query parameters, varies (TODO improve this)
 * @param {abortCallback} config.abortMethod Callback for when a request has been aborted
 */
const getIncidents = (params: any, config: PagingApiAndNotifierConfig = {}) => {
  //TODO:TS
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

const getIncidentById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const getNewIncident = async () => {
  //optionally make any other changes as necessary before returning.
  return { data: IncidentService.getInitialData() } as AxiosResponse<IncidentNew, any>;
};

const createIncident = (Incident: Incident, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.post(URL, Incident, {
    successNotifCb,
    errorNotifCb,
  });
};

const updateIncident = (Incident: Incident, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.put(URL + "/" + Incident.id, Incident, {
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {Incident} Incident This cannot be a 'ref' to a Incident
 */
const saveIncident = (Incident: Incident, config: PagingApiAndNotifierConfig = {}) => {
  if (Incident.id) {
    return updateIncident(Incident, config);
  } else {
    return createIncident(Incident, config);
  }
};

const deleteIncidentById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const canDeleteIncident = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return canDeleteApi.delete(URL + "/" + id + "?" + "validateOnly=" + true, {
    successNotifCb,
    errorNotifCb,
  });
};

export {
  canDeleteIncident, createIncident, deleteIncidentById, getIncidentById, getIncidents, getNewIncident, saveIncident, updateIncident
};

